import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ListTodo, Clock, CheckCircle2, Upload, Users, Building2, AlertCircle, ArrowRight, MessageCircle, Sparkles, Calendar, Inbox, Send, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useActiveCohort, withCohort } from "@/lib/cohort-context";

const BASE = import.meta.env.BASE_URL;

const OPEN_THREAD_STATES = new Set(["sent", "replied", "no_reply"]);

function timeAgo(d: string | Date): string {
  const t = new Date(d).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString();
}

export default function BankerDashboard() {
  const [stats, setStats] = useState<any>(null);
  const [recentTasks, setRecentTasks] = useState<any[]>([]);
  const [today, setToday] = useState<any[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const { cohortId } = useActiveCohort();

  const loadConversations = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) { setConversationsLoading(false); return; }
    try {
      const r = await fetch(`${BASE}api/banker/outreach`, { headers: { "x-banker-token": token } });
      if (r.ok) {
        const data = await r.json();
        // Only open threads. Sort: needs-reply (last msg from investor) first,
        // then by most recent message timestamp.
        const open = (data as any[]).filter((o) => OPEN_THREAD_STATES.has(o.status));
        open.sort((a, b) => {
          const aLast = a.messages?.[a.messages.length - 1];
          const bLast = b.messages?.[b.messages.length - 1];
          const aNeeds = aLast?.senderRole === "investor" ? 1 : 0;
          const bNeeds = bLast?.senderRole === "investor" ? 1 : 0;
          if (aNeeds !== bNeeds) return bNeeds - aNeeds;
          const aT = new Date(aLast?.createdAt || a.updatedAt).getTime();
          const bT = new Date(bLast?.createdAt || b.updatedAt).getTime();
          return bT - aT;
        });
        setConversations(open);
      }
    } catch {}
    setConversationsLoading(false);
  };

  useEffect(() => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    (async () => {
      try {
        const [sRes, tRes, cRes] = await Promise.all([
          fetch(`${BASE}api/banker/dashboard`, { headers: { "x-banker-token": token } }),
          fetch(withCohort(`${BASE}api/banker/tasks?scope=mine`, cohortId), { headers: { "x-banker-token": token } }),
          fetch(`${BASE}api/banker/me/calendar/today`, { headers: { "x-banker-token": token } }),
        ]);
        if (sRes.ok) setStats(await sRes.json());
        if (tRes.ok) {
          const t = await tRes.json();
          setRecentTasks(t.filter((x: any) => x.status !== "closed").slice(0, 6));
        }
        if (cRes.ok) setToday(await cRes.json());
        await loadConversations();
      } catch {}
      setLoading(false);
    })();
  }, [cohortId]);

  const priorityColor: any = { high: "text-red-400 bg-red-500/15 border-red-500/30", medium: "text-amber-400 bg-amber-500/15 border-amber-500/30", low: "text-white/50 bg-white/5 border-white/10" };
  const statusColor: any = { pending: "text-blue-400", in_progress: "text-amber-400", awaiting_founder: "text-purple-400", artifact_uploaded: "text-emerald-400", closed: "text-white/40" };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Portfolio Desk</h1>
        <p className="text-white/50 text-sm mt-1">Customer success for your covered companies and the investors backing them. Your queue, your coverage, your momentum.</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white/[0.04] border border-white/[0.08] rounded-xl animate-pulse" />)}</div>
      ) : stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard icon={ListTodo} label="Pending" value={stats.tasksPending} color="blue" />
            <StatCard icon={Clock} label="In Progress" value={stats.tasksInProgress} color="amber" />
            <StatCard icon={AlertCircle} label="Awaiting Founder" value={stats.tasksAwaiting} color="purple" />
            <StatCard icon={AlertCircle} label="Reopened" value={stats.tasksReopened ?? 0} color={(stats.tasksReopened ?? 0) > 0 ? "amber" : "blue"} />
            <StatCard icon={CheckCircle2} label="Closed This Week" value={stats.closedThisWeek} color="emerald" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard icon={Users} label="Assigned Investors" value={stats.investors} color="orange" />
            <StatCard icon={Building2} label="Companies Covered" value={stats.companies} color="orange" />
            <StatCard icon={Upload} label="Artifacts This Week" value={stats.artifactsThisWeek} color="orange" />
          </div>
        </>
      )}

      <div className="bg-gradient-to-br from-[#FF5C00]/[0.06] to-transparent border border-[#FF5C00]/25 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-[#FF5C00]" /> Today</h2>
          <Link href="/banker/calendar"><a className="text-[#FF5C00] text-xs flex items-center gap-1 hover:underline">Open calendar <ArrowRight className="w-3 h-3" /></a></Link>
        </div>
        {today.length === 0 ? (
          <p className="text-white/45 text-sm">Nothing on your calendar today. Quiet morning — use it to clear the inbox.</p>
        ) : (
          <div className="space-y-2">
            {today.map((e: any) => (
              <div key={e.id} className="flex items-center gap-3 bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
                <div className="text-white/70 font-mono text-xs w-14 flex-shrink-0">
                  {new Date(e.startsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{e.title}</p>
                  <p className="text-white/45 text-xs">{e.kind}{e.company ? ` · ${e.company.name}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConversationsPanel conversations={conversations} loading={conversationsLoading} onChange={loadConversations} />

      <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">Recent open tasks</h2>
          <Link href="/banker/tasks"><a className="text-[#FF5C00] text-sm flex items-center gap-1 hover:underline">View all <ArrowRight className="w-3 h-3" /></a></Link>
        </div>
        {recentTasks.length === 0 ? (
          <p className="text-white/40 text-sm">No open tasks. Meera will push new ones here as investors chat with Kavya.</p>
        ) : (
          <div className="space-y-2">
            {recentTasks.map((t) => (
              <Link key={t.id} href={`/banker/tasks/${t.id}`}>
                <a className="block bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] hover:border-white/[0.15] rounded-lg p-4 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${priorityColor[t.priority] || priorityColor.medium}`}>{t.priority}</span>
                        <span className={`text-[10px] uppercase tracking-wider ${statusColor[t.status]}`}>{t.status.replace("_", " ")}</span>
                      </div>
                      <p className="text-white text-sm font-medium truncate">{t.title}</p>
                      <p className="text-white/50 text-xs mt-1 truncate">
                        {t.investor?.name || "—"}{t.company ? ` · ${t.company.name}` : ""}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/30 mt-1 flex-shrink-0" />
                  </div>
                </a>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gradient-to-br from-[#FF5C00]/10 to-transparent border border-[#FF5C00]/20 rounded-2xl p-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-[#FF5C00]/20 border border-[#FF5C00]/30 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-5 h-5 text-[#FF5C00]" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-white">Meera is standing by.</p>
          <p className="text-white/60 text-sm">Ask about any investor, any company, or how to draft your next founder outreach.</p>
        </div>
        <Link href="/banker/meera"><a className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap">Ask Meera</a></Link>
      </div>

      <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/25 rounded-2xl p-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-purple-300" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-white">Viren is your senior banking lead.</p>
          <p className="text-white/60 text-sm">Open any task and tap <span className="text-purple-300 font-medium">Prep questions</span> — Viren reads Meera's brief, the artifacts so far, and writes 4–7 sharp questions for the founder.</p>
        </div>
        <Link href="/banker/tasks"><a className="border border-purple-500/40 hover:border-purple-400/60 text-purple-200 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap">Go to tasks</a></Link>
      </div>
    </div>
  );
}

// Email-style inbox of all open advisor↔investor conversations.
// Click a row to expand the thread inline, type a reply, send. No modal,
// no navigation. Threads with the most recent message from the investor
// (i.e. waiting for the advisor) are pinned to the top with a "Needs reply" pill.
function ConversationsPanel({ conversations, loading, onChange }: { conversations: any[]; loading: boolean; onChange: () => void }) {
  const [openId, setOpenId] = useState<number | null>(null);
  const [replyDraft, setReplyDraft] = useState<Record<number, string>>({});
  // Per-thread send state. A scalar `sendingId` would race when the advisor
  // sends on thread A and immediately on thread B — first completion would
  // wrongly clear the spinner on the still-pending second send.
  const [sendingMap, setSendingMap] = useState<Record<number, boolean>>({});
  const [errMap, setErrMap] = useState<Record<number, string>>({});

  const needsReplyCount = conversations.filter((c) => {
    const last = c.messages?.[c.messages.length - 1];
    return last?.senderRole === "investor";
  }).length;

  const sendReply = async (id: number) => {
    const body = (replyDraft[id] || "").trim();
    if (!body) return;
    if (sendingMap[id]) return; // guard double-send on the same thread
    setSendingMap((m) => ({ ...m, [id]: true }));
    setErrMap((m) => ({ ...m, [id]: "" }));
    const token = localStorage.getItem("banker_token");
    try {
      const r = await fetch(`${BASE}api/banker/outreach/${id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify({ body }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        setErrMap((m) => ({ ...m, [id]: e.error || "Reply failed" }));
      } else {
        setReplyDraft((d) => ({ ...d, [id]: "" }));
        await onChange();
      }
    } catch {
      setErrMap((m) => ({ ...m, [id]: "Network error" }));
    }
    setSendingMap((m) => {
      const next = { ...m };
      delete next[id];
      return next;
    });
  };

  return (
    <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-lg flex items-center gap-2">
          <Inbox className="w-5 h-5 text-[#FF5C00]" /> Inbox
          {needsReplyCount > 0 && (
            <span className="bg-[#FF5C00] text-white text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full">
              {needsReplyCount} need{needsReplyCount === 1 ? "s" : ""} reply
            </span>
          )}
        </h2>
        <p className="text-white/45 text-xs font-mono">{conversations.length} open</p>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-16 bg-white/[0.03] border border-white/[0.08] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <p className="text-white/45 text-sm">No open conversations. When investors reply to your outreach, the threads land here.</p>
      ) : (
      <div className="space-y-2">
        {conversations.map((c) => {
          const last = c.messages?.[c.messages.length - 1];
          const needsReply = last?.senderRole === "investor";
          const isOpen = openId === c.id;
          const panelId = `conversation-panel-${c.id}`;
          return (
            <div key={c.id} className={`bg-white/[0.03] border rounded-xl transition-colors ${needsReply ? "border-[#FF5C00]/40" : "border-white/[0.08]"}`}>
              <button
                onClick={() => setOpenId(isOpen ? null : c.id)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="w-full text-left p-4 flex items-start gap-3 hover:bg-white/[0.02]"
              >
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${needsReply ? "bg-[#FF5C00]" : "bg-white/20"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-white text-sm font-medium truncate">{c.investor?.name || "Investor"}</span>
                    {needsReply && (
                      <span className="text-[10px] uppercase tracking-wider font-mono text-[#FF5C00] bg-[#FF5C00]/15 border border-[#FF5C00]/30 px-1.5 py-0.5 rounded">
                        Investor replied
                      </span>
                    )}
                    <span className="text-white/30 text-xs ml-auto whitespace-nowrap">{timeAgo(last?.createdAt || c.updatedAt)}</span>
                  </div>
                  <p className="text-white/60 text-xs truncate mb-1">{c.subject}</p>
                  <p className="text-white/45 text-xs truncate">
                    <span className="text-white/55">{last?.senderRole === "investor" ? `${c.investor?.name?.split(" ")[0] || "Investor"}: ` : last?.senderRole === "kavya" ? "Kavya: " : "You: "}</span>
                    {(last?.body || "").replace(/\s+/g, " ").slice(0, 110)}
                  </p>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-white/40 mt-1 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-white/40 mt-1 flex-shrink-0" />}
              </button>

              {isOpen && (
                <div id={panelId} className="border-t border-white/[0.06] p-4 space-y-3">
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {c.messages?.map((m: any) => {
                      const fromInvestor = m.senderRole === "investor";
                      return (
                        <div
                          key={m.id}
                          className={`rounded-lg p-3 border ${fromInvestor ? "bg-[#FF5C00]/[0.08] border-[#FF5C00]/30" : "bg-white/[0.04] border-white/[0.08]"}`}
                        >
                          <p className="text-[10px] uppercase tracking-wider font-mono mb-1 flex items-center gap-2">
                            <span className={fromInvestor ? "text-[#FF5C00]" : "text-white/45"}>
                              {fromInvestor ? `${c.investor?.name || "Investor"}` : m.senderRole === "kavya" ? "Kavya (nudge)" : "You"}
                            </span>
                            <span className="text-white/30">{new Date(m.createdAt).toLocaleString()}</span>
                          </p>
                          <p className="text-white/85 text-sm whitespace-pre-wrap">{m.body}</p>
                        </div>
                      );
                    })}
                  </div>
                  {errMap[c.id] && (
                    <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-300 text-xs">{errMap[c.id]}</div>
                  )}
                  <div>
                    <label htmlFor={`reply-${c.id}`} className="sr-only">
                      Reply to {c.investor?.name || "investor"}
                    </label>
                    <textarea
                      id={`reply-${c.id}`}
                      value={replyDraft[c.id] || ""}
                      onChange={(e) => setReplyDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                      rows={3}
                      maxLength={4000}
                      placeholder={`Reply to ${c.investor?.name?.split(" ")[0] || "investor"}…`}
                      className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 resize-none focus:border-[#FF5C00]/50 outline-none"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-white/35 text-[11px]">Goes straight to the investor. No admin approval on follow-ups.</p>
                      <button
                        onClick={() => sendReply(c.id)}
                        disabled={!!sendingMap[c.id] || !(replyDraft[c.id] || "").trim()}
                        aria-label={`Send reply to ${c.investor?.name || "investor"}`}
                        className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm px-4 py-1.5 rounded-lg flex items-center gap-1.5"
                      >
                        {sendingMap[c.id] ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending</> : <><Send className="w-3.5 h-3.5" /> Send reply</>}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }: any) {
  const colors: any = {
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    purple: "text-purple-400 bg-purple-500/10 border-purple-500/20",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    orange: "text-[#FF5C00] bg-[#FF5C00]/10 border-[#FF5C00]/20",
  };
  return (
    <div className={`bg-white/[0.04] border rounded-xl p-4 ${colors[color].split(" ")[2]}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-2xl font-bold font-mono">{value}</p>
      <p className="text-white/50 text-xs mt-0.5">{label}</p>
    </div>
  );
}
